import React from "react";
import { Flame, Sparkles, CircleDot } from "lucide-react";
import {
  fmtCompact,
  fmtOdds,
  getHouseBettorsTotal,
  getHousePoolTotal,
  getFeaturedFixture,
  computeOddsPercents,
  isFixtureLive,
  isFixtureSoon,
  isFreshHouse,
} from "../../../utils/bondhouseHelpers";

const getInitials = (name = "") => {
  const trimmed = name.trim();
  if (!trimmed) return "??";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const FloorRow = ({ house, selected, onSelect, heatPct = 0, rank }) => {
  const bettors = getHouseBettorsTotal(house);
  const pool = getHousePoolTotal(house);
  const fixtures = house?.fixtures?.length || 0;
  const featured = getFeaturedFixture(house);
  const fresh = isFreshHouse(house);
  const hot = bettors > 0;
  const liveNow = featured ? isFixtureLive(featured) : false;
  const soon = featured && !liveNow && isFixtureSoon(featured);

  const home = featured?.teams?.home?.name || "—";
  const away = featured?.teams?.away?.name || "—";
  const percents = featured ? computeOddsPercents(featured?.odds) : null;
  const o =
    featured?.odds?.oneXTwo?.fullTime || featured?.odds?.oneXTwo?.firstHalf;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`floor-row ${selected ? "floor-row-selected" : ""}`}
      aria-pressed={selected}
    >
      <span
        className="floor-row-heat"
        style={{ "--heat": `${Math.max(8, heatPct)}%` }}
        aria-hidden
      />
      <span className="floor-row-rank" aria-hidden>
        {String(rank).padStart(2, "0")}
      </span>
      <span className="floor-row-avatar">
        <span>{getInitials(house?.name)}</span>
        {liveNow && <span className="floor-row-live-dot" aria-hidden />}
      </span>
      <span className="floor-row-body">
        <span className="floor-row-top">
          <span className="floor-row-name" title={house?.name}>
            {house?.name || "Unnamed"}
          </span>
          <span className="floor-row-tags">
            {liveNow && (
              <span className="floor-row-tag floor-row-tag-live">
                <CircleDot size={8} />
              </span>
            )}
            {soon && <span className="floor-row-tag floor-row-tag-soon">Soon</span>}
            {hot && !liveNow && (
              <span className="floor-row-tag floor-row-tag-hot" title="Active">
                <Flame size={8} />
              </span>
            )}
            {fresh && (
              <span className="floor-row-tag floor-row-tag-fresh" title="New">
                <Sparkles size={8} />
              </span>
            )}
          </span>
        </span>
        <span className="floor-row-match">
          <span className="floor-row-team">{home}</span>
          <span className="floor-row-vs">·</span>
          <span className="floor-row-team">{away}</span>
        </span>
        <span className="floor-row-bottom">
          <span className="floor-row-metrics">
            <span>{fixtures} fx</span>
            <span>{fmtCompact(pool)}</span>
            <span>{bettors} bet</span>
          </span>
          {o && (
            <span className="floor-row-odds" aria-hidden>
              <span>{fmtOdds(o["1"])}</span>
              <span>{fmtOdds(o.X)}</span>
              <span>{fmtOdds(o["2"])}</span>
            </span>
          )}
        </span>
        {percents && (
          <span className="floor-row-implied" aria-hidden>
            <span style={{ flex: percents.home || 1 }} />
            <span style={{ flex: percents.draw || 1 }} />
            <span style={{ flex: percents.away || 1 }} />
          </span>
        )}
      </span>
    </button>
  );
};

export default FloorRow;
